import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dprAPI } from '../api';
import { toast } from 'sonner';
import { ArrowLeft, Download, Printer, Calendar as CalendarIcon, User as UserIcon } from 'lucide-react';
import { format } from 'date-fns';

const fmtCurrency = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';

const Section = ({ title, data, format: fmt }) => (
  <div className="clay-card p-5">
    <h3 className="font-outfit font-medium text-lg mb-3">{title}</h3>
    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
      {Object.entries(data).map(([k, v]) => (
        <React.Fragment key={k}>
          <span className="text-muted-foreground capitalize">{k.replace(/_/g, ' ')}</span>
          <span className="font-medium text-right">{fmt ? fmt(v) : v}</span>
        </React.Fragment>
      ))}
    </div>
  </div>
);

export const DPRPage = () => {
  const [dpr, setDpr] = useState(null);
  const [teamUsers, setTeamUsers] = useState([]);
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [userId, setUserId] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchDpr = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date };
      if (userId) params.user_id = userId;
      const { data } = await dprAPI.get(params);
      setDpr(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load DPR');
    } finally {
      setLoading(false);
    }
  }, [date, userId]);

  useEffect(() => {
    (async () => {
      try { const { data } = await dprAPI.getTeamUsers(); setTeamUsers(data); } catch {}
    })();
  }, []);

  useEffect(() => { fetchDpr(); }, [fetchDpr]);

  const handleExport = async (fmt) => {
    try {
      const { data } = await dprAPI.export({ date, user_id: userId || undefined, export_format: fmt });
      const blob = new Blob([data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DPR_${date}.${fmt === 'xlsx' ? 'xlsx' : 'csv'}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Exported');
    } catch { toast.error('Export failed'); }
  };

  return (
    <div className="space-y-6" data-testid="dpr-container">
      <div className="flex items-center gap-4 print:hidden">
        <button onClick={() => navigate('/reports')} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-3xl font-outfit font-semibold">Daily Progress Report</h1>
          <p className="text-muted-foreground">Auto-calculated CRM activity summary</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => handleExport('csv')} data-testid="dpr-export-csv" className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm inline-flex items-center gap-1"><Download className="h-4 w-4" /> CSV</button>
          <button onClick={() => handleExport('xlsx')} data-testid="dpr-export-xlsx" className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm inline-flex items-center gap-1"><Download className="h-4 w-4" /> Excel</button>
          <button onClick={() => window.print()} data-testid="dpr-export-print" className="px-3 py-2 bg-primary text-white rounded-lg text-sm inline-flex items-center gap-1"><Printer className="h-4 w-4" /> Print / PDF</button>
        </div>
      </div>

      <div className="clay-card p-4 flex flex-wrap items-end gap-3 print:hidden">
        <div>
          <label className="block text-xs font-medium mb-1 flex items-center gap-1"><CalendarIcon className="h-3 w-3" /> Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="dpr-date-input" className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
        </div>
        {teamUsers.length > 1 && (
          <div>
            <label className="block text-xs font-medium mb-1 flex items-center gap-1"><UserIcon className="h-3 w-3" /> Employee</label>
            <select value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="dpr-user-select" className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm min-w-[220px]">
              <option value="">— select employee —</option>
              {teamUsers.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role.replace(/_/g, ' ')})</option>)}
            </select>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div></div>
      ) : dpr && dpr.employee.id ? (
        <div className="space-y-4" data-testid="dpr-content">
          {/* Header card */}
          <div className="clay-card p-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><p className="text-muted-foreground">Employee</p><p className="font-medium">{dpr.employee.name}</p></div>
              <div><p className="text-muted-foreground">Employee ID</p><p className="font-medium">{dpr.employee.employee_id || '—'}</p></div>
              <div><p className="text-muted-foreground">Reporting Manager</p><p className="font-medium">{dpr.employee.reporting_manager || '—'}</p></div>
              <div><p className="text-muted-foreground">Date</p><p className="font-medium">{format(new Date(dpr.date), 'MMM d, yyyy')}</p></div>
              <div><p className="text-muted-foreground">Login Time</p><p className="font-medium">{fmtTime(dpr.login_time)}</p></div>
              <div><p className="text-muted-foreground">Logout Time</p><p className="font-medium">{fmtTime(dpr.logout_time)}</p></div>
              <div><p className="text-muted-foreground">Working Hours</p><p className="font-medium">{dpr.working_hours} hrs</p></div>
              <div><p className="text-muted-foreground">Role</p><p className="font-medium capitalize">{dpr.employee.role?.replace(/_/g, ' ')}</p></div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Section title="Leads" data={dpr.leads} />
            <Section title="Follow-ups" data={dpr.follow_ups} />
            <Section title="Calls" data={dpr.calls} />
            <Section title="Site Visits" data={dpr.site_visits} />
            <Section title="Quotations" data={dpr.quotations} />
            <Section title="Revenue" data={dpr.revenue} format={fmtCurrency} />
          </div>
        </div>
      ) : (
        <div className="clay-card p-8 text-center text-sm text-muted-foreground">Select an employee to view their DPR.</div>
      )}
    </div>
  );
};

export default DPRPage;
